import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Calculator, Sparkles, Users, Heart, ArrowRight, SkipForward, Compass, Lock, UserPlus } from 'lucide-react';
import { calculateAllNumbers } from '@/utils/numerologyCalculator';
import BirthDateInfoModal, { hasSeenBirthDateInfo } from '@/components/BirthDateInfoModal';
import { useAuth } from '@/context/AuthContext';
import AuthModal from '@/components/AuthModal';

// Guest calculation tracking constants
const GUEST_CALC_KEY = 'guestCalculations';
const MAX_GUEST_CALCULATIONS = 2;

// Virtuali lietuviškų raidžių klaviatūra
const LithuanianKeyboard = ({ onInsert }) => {
  const letters = ['ą', 'č', 'ę', 'ė', 'į', 'š', 'ų', 'ū', 'ž', 'Ą', 'Č', 'Ę', 'Ė', 'Į', 'Š', 'Ų', 'Ū', 'Ž'];
  
  return (
    <div className="flex flex-wrap gap-1.5 justify-center mt-3 p-3 bg-secondary/30 rounded-lg border border-border/50">
      {letters.map((letter) => (
        <button
          key={letter}
          type="button"
          onClick={() => onInsert(letter)}
          className="w-8 h-8 rounded bg-amber-500/20 hover:bg-amber-500/40 text-amber-400 font-medium text-sm transition-all duration-200 border border-amber-500/30 hover:scale-110"
        >
          {letter}
        </button>
      ))}
    </div>
  );
};

export default function CalculatorPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [step, setStep] = useState(1); // 1 = User data, 2 = Partner question
  const [showBirthDateInfo, setShowBirthDateInfo] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showGuestLimitModal, setShowGuestLimitModal] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    day: '',
    month: '',
    year: '',
    gender: '' // 'female' or 'male'
  });
  const [partnerData, setPartnerData] = useState({
    firstName: '',
    lastName: '',
    day: '',
    month: '',
    year: ''
  });
  const [includePartner, setIncludePartner] = useState(null);
  const [activeField, setActiveField] = useState('firstName');
  const [partnerActiveField, setPartnerActiveField] = useState('firstName');
  
  // Refs for input fields
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const partnerFirstNameRef = useRef(null);
  const partnerLastNameRef = useRef(null);

  // Check if user has seen birth date info modal on direct page access
  useEffect(() => {
    if (!hasSeenBirthDateInfo()) {
      setShowBirthDateInfo(true);
    }
  }, []);

  // Handle confirmation from birth date info modal
  const handleBirthDateInfoConfirm = () => {
    setShowBirthDateInfo(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePartnerChange = (e) => {
    setPartnerData({
      ...partnerData,
      [e.target.name]: e.target.value
    });
  };

  // Įterpti lietuvišką raidę į aktyvų laukelį
  const insertLithuanianLetter = (letter) => {
    if (activeField === 'firstName') {
      setFormData(prev => ({
        ...prev,
        firstName: prev.firstName + letter
      }));
      firstNameRef.current?.focus();
    } else if (activeField === 'lastName') {
      setFormData(prev => ({
        ...prev,
        lastName: prev.lastName + letter
      }));
      lastNameRef.current?.focus();
    }
  };

  // Įterpti lietuvišką raidę į partnerio laukelį
  const insertPartnerLithuanianLetter = (letter) => {
    if (partnerActiveField === 'firstName') {
      setPartnerData(prev => ({
        ...prev,
        firstName: prev.firstName + letter
      }));
      partnerFirstNameRef.current?.focus();
    } else if (partnerActiveField === 'lastName') {
      setPartnerData(prev => ({
        ...prev,
        lastName: prev.lastName + letter
      }));
      partnerLastNameRef.current?.focus();
    }
  };

  const validateUserData = () => {
    if (!formData.firstName || !formData.lastName) {
      toast.error('Prašome įvesti vardą ir pavardę');
      return false;
    }
    
    const day = parseInt(formData.day);
    const month = parseInt(formData.month);
    const year = parseInt(formData.year);
    
    if (!day || !month || !year) {
      toast.error('Prašome įvesti teisingą gimimo datą');
      return false;
    }
    
    if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900 || year > new Date().getFullYear()) {
      toast.error('Neteisingos gimimo datos reikšmės');
      return false;
    }
    
    return true;
  };

  const handleStep1Submit = (e) => {
    e.preventDefault();
    if (validateUserData()) {
      setStep(2);
    }
  };

  const handleFinalSubmit = (withPartner = false) => {
    // Check guest limit for unauthenticated users
    if (!isAuthenticated) {
      const storedCalcs = localStorage.getItem(GUEST_CALC_KEY);
      const guestCalcs = storedCalcs ? JSON.parse(storedCalcs) : [];
      const nameKey = `${formData.firstName.toLowerCase()}_${formData.lastName.toLowerCase()}`;
      
      // Check if this is a new unique name
      if (!guestCalcs.includes(nameKey)) {
        // Check if limit reached
        if (guestCalcs.length >= MAX_GUEST_CALCULATIONS) {
          setShowGuestLimitModal(true);
          return;
        }
        // Add new name to guest calculations
        guestCalcs.push(nameKey);
        localStorage.setItem(GUEST_CALC_KEY, JSON.stringify(guestCalcs));
      }
    }
    
    // Calculate user numbers
    const results = calculateAllNumbers(
      formData.firstName,
      formData.lastName,
      formData.day,
      formData.month,
      formData.year
    );
    
    const dataToStore = {
      ...formData,
      ...results
    };

    // If including partner, calculate their numbers too
    if (withPartner && partnerData.firstName && partnerData.day) {
      const partnerResults = calculateAllNumbers(
        partnerData.firstName,
        partnerData.lastName,
        partnerData.day,
        partnerData.month,
        partnerData.year
      );
      
      dataToStore.partner = {
        ...partnerData,
        ...partnerResults
      };
    }
    
    // Store in localStorage
    localStorage.setItem('numerologyData', JSON.stringify(dataToStore));
    
    toast.success('Skaičiavimas atliktas sėkmingai!');
    navigate('/rezultatai');
  };

  const handlePartnerSubmit = (e) => {
    e.preventDefault();
    
    if (!partnerData.firstName || !partnerData.lastName) {
      toast.error('Prašome įvesti partnerio vardą ir pavardę');
      return;
    }
    
    const day = parseInt(partnerData.day);
    const month = parseInt(partnerData.month);
    const year = parseInt(partnerData.year);
    
    if (!day || !month || !year || day < 1 || day > 31 || month < 1 || month > 12) {
      toast.error('Neteisingos partnerio gimimo datos reikšmės');
      return;
    }
    
    handleFinalSubmit(true);
  };

  const handleSkipPartner = () => {
    handleFinalSubmit(false);
  };

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => step === 1 ? navigate('/') : setStep(1)}
            className="mb-4 text-foreground/70 hover:text-amber-400"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {step === 1 ? 'Grįžti' : 'Atgal'}
          </Button>
          <div className="text-center">
            <div className="inline-flex items-center justify-center gap-2 mb-4">
              <Compass className="w-8 h-8 text-amber-400" />
              <h1 className="font-display text-3xl sm:text-4xl bg-gradient-to-r from-amber-400 to-purple-400 bg-clip-text text-transparent">NUMENAXIS</h1>
            </div>
            <p className="text-muted-foreground text-base">
              {step === 1 
                ? 'Įveskite savo duomenis ir pradėkite pažinties su savimi kelionę'
                : 'Partnerystės analizė'
              }
            </p>
            
            {/* Progress indicator */}
            <div className="flex justify-center gap-2 mt-4">
              <div className={`w-3 h-3 rounded-full ${step >= 1 ? 'bg-amber-400' : 'bg-gray-600'}`} />
              <div className={`w-3 h-3 rounded-full ${step >= 2 ? 'bg-amber-400' : 'bg-gray-600'}`} />
            </div>
          </div>
        </div>

        {/* Step 1: User Data */}
        {step === 1 && (
          <Card className="card-mystical p-8 shadow-card border-border/50">
            <form onSubmit={handleStep1Submit} className="space-y-6">
              {/* Name Fields */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="firstName" className="text-foreground font-medium mb-2 block">
                    Vardas *
                  </Label>
                  <Input
                    ref={firstNameRef}
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    onFocus={() => setActiveField('firstName')}
                    placeholder="Įveskite savo vardą"
                    className="bg-input border-border text-foreground placeholder:text-muted-foreground"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="lastName" className="text-foreground font-medium mb-2 block">
                    Pavardė *
                  </Label>
                  <Input
                    ref={lastNameRef}
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    onFocus={() => setActiveField('lastName')}
                    placeholder="Įveskite savo pavardę"
                    className="bg-input border-border text-foreground placeholder:text-muted-foreground"
                    required
                  />
                </div>
              </div>

              {/* Virtual Lithuanian Keyboard - visible on all devices */}
              <div>
                <p className="text-xs text-center text-muted-foreground mb-1">
                  Lietuviškos raidės (paspauskite norėdami įterpti):
                </p>
                <LithuanianKeyboard onInsert={insertLithuanianLetter} />
              </div>

              {/* Birth Date */}
              <div>
                <Label className="text-foreground font-medium mb-3 block">
                  Tavo gimimo data *
                </Label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="day" className="text-sm text-muted-foreground mb-2 block">
                      Diena
                    </Label>
                    <Input
                      id="day"
                      name="day"
                      type="number"
                      min="1"
                      max="31"
                      value={formData.day}
                      onChange={handleChange}
                      placeholder="DD"
                      className="bg-input border-border text-foreground placeholder:text-muted-foreground text-center"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="month" className="text-sm text-muted-foreground mb-2 block">
                      Mėnuo
                    </Label>
                    <Input
                      id="month"
                      name="month"
                      type="number"
                      min="1"
                      max="12"
                      value={formData.month}
                      onChange={handleChange}
                      placeholder="MM"
                      className="bg-input border-border text-foreground placeholder:text-muted-foreground text-center"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="year" className="text-sm text-muted-foreground mb-2 block">
                      Metai
                    </Label>
                    <Input
                      id="year"
                      name="year"
                      type="number"
                      min="1900"
                      max={new Date().getFullYear()}
                      value={formData.year}
                      onChange={handleChange}
                      placeholder="YYYY"
                      className="bg-input border-border text-foreground placeholder:text-muted-foreground text-center"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Gender Selection */}
              <div>
                <Label className="text-foreground font-medium mb-3 block">
                  Kaip į tave kreiptis?
                </Label>
                <div className="flex gap-4">
                  <label className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                    formData.gender === 'female' 
                      ? 'border-purple-500 bg-purple-500/10 text-purple-400' 
                      : 'border-border/50 bg-secondary/10 hover:border-purple-500/50 text-foreground/80'
                  }`}>
                    <input
                      type="radio"
                      name="gender"
                      value="female"
                      checked={formData.gender === 'female'}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <span className="text-lg">♀</span>
                    <span className="font-medium">Moteris</span>
                  </label>
                  
                  <label className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                    formData.gender === 'male' 
                      ? 'border-blue-500 bg-blue-500/10 text-blue-400' 
                      : 'border-border/50 bg-secondary/10 hover:border-blue-500/50 text-foreground/80'
                  }`}>
                    <input
                      type="radio"
                      name="gender"
                      value="male"
                      checked={formData.gender === 'male'}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <span className="text-lg">♂</span>
                    <span className="font-medium">Vyras</span>
                  </label>
                </div>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Šis pasirinkimas naudojamas tik pasisveikinimo kalbai.
                </p>
              </div>

              {/* Info Note - Updated */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-foreground/80 leading-relaxed">
                    <p className="mb-2 font-semibold text-amber-400">Visi lietuviški simboliai yra palaikomi</p>
                    <p>
                      Algoritmas naudoja unikalią tavo vardo raidžių vibraciją, todėl rašyk vardą tiksliai su lietuviškomis raidėmis.
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold py-6 text-lg transition-all duration-300"
              >
                Tęsti
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </form>
          </Card>
        )}

        {/* Step 2: Partner Question */}
        {step === 2 && includePartner === null && (
          <Card className="card-mystical p-8 shadow-card border-border/50">
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-pink-500/20 rounded-full flex items-center justify-center mx-auto border-2 border-pink-500/40">
                <Heart className="w-10 h-10 text-pink-400" />
              </div>
              
              <div>
                <h2 className="font-display text-2xl text-amber-400 mb-2">
                  Partnerystės analizė
                </h2>
                <p className="text-foreground/80">
                  Ar norėtum pridėti partnerio duomenis ir gauti išsamią suderinamumo analizę?
                </p>
              </div>
              
              <div className="space-y-3">
                <Button
                  onClick={() => setIncludePartner(true)}
                  size="lg"
                  className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-semibold py-5"
                >
                  <Users className="w-5 h-5 mr-2" />
                  Taip, pridėti partnerį
                </Button>
                
                <Button
                  onClick={handleSkipPartner}
                  variant="outline"
                  size="lg"
                  className="w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/10 py-5"
                >
                  <SkipForward className="w-5 h-5 mr-2" />
                  Praleisti ir tęsti
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground">
                Partnerio duomenis galėsi pridėti ir vėliau
              </p>
            </div>
          </Card>
        )}

        {/* Step 2: Partner Data Form */}
        {step === 2 && includePartner === true && (
          <Card className="card-mystical p-8 shadow-card border-border/50">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-pink-500/40">
                <Heart className="w-8 h-8 text-pink-400" />
              </div>
              <h2 className="font-display text-xl text-amber-400">
                Partnerio duomenys
              </h2>
            </div>
            
            <form onSubmit={handlePartnerSubmit} className="space-y-6">
              {/* Partner Name Fields */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="partnerFirstName" className="text-foreground font-medium mb-2 block">
                    Partnerio vardas *
                  </Label>
                  <Input
                    ref={partnerFirstNameRef}
                    id="partnerFirstName"
                    name="firstName"
                    value={partnerData.firstName}
                    onChange={handlePartnerChange}
                    onFocus={() => setPartnerActiveField('firstName')}
                    placeholder="Įveskite partnerio vardą"
                    className="bg-input border-border text-foreground placeholder:text-muted-foreground"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="partnerLastName" className="text-foreground font-medium mb-2 block">
                    Partnerio pavardė *
                  </Label>
                  <Input
                    ref={partnerLastNameRef}
                    id="partnerLastName"
                    name="lastName"
                    value={partnerData.lastName}
                    onChange={handlePartnerChange}
                    onFocus={() => setPartnerActiveField('lastName')}
                    placeholder="Įveskite partnerio pavardę"
                    className="bg-input border-border text-foreground placeholder:text-muted-foreground"
                    required
                  />
                </div>
              </div>

              {/* Virtual Lithuanian Keyboard for Partner */}
              <div className="hidden sm:block">
                <p className="text-xs text-center text-muted-foreground mb-1">
                  Lietuviškos raidės (paspauskite norėdami įterpti):
                </p>
                <LithuanianKeyboard onInsert={insertPartnerLithuanianLetter} />
              </div>

              {/* Partner Birth Date */}
              <div>
                <Label className="text-foreground font-medium mb-3 block">
                  Partnerio gimimo data *
                </Label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Diena</Label>
                    <Input
                      name="day"
                      type="number"
                      min="1"
                      max="31"
                      value={partnerData.day}
                      onChange={handlePartnerChange}
                      placeholder="DD"
                      className="bg-input border-border text-foreground placeholder:text-muted-foreground text-center"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Mėnuo</Label>
                    <Input
                      name="month"
                      type="number"
                      min="1"
                      max="12"
                      value={partnerData.month}
                      onChange={handlePartnerChange}
                      placeholder="MM"
                      className="bg-input border-border text-foreground placeholder:text-muted-foreground text-center"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Metai</Label>
                    <Input
                      name="year"
                      type="number"
                      min="1900"
                      max={new Date().getFullYear()}
                      value={partnerData.year}
                      onChange={handlePartnerChange}
                      placeholder="YYYY"
                      className="bg-input border-border text-foreground placeholder:text-muted-foreground text-center"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="space-y-3">
                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold py-5"
                >
                  <Calculator className="w-5 h-5 mr-2" />
                  Skaičiuoti abu
                </Button>
                
                <Button
                  type="button"
                  onClick={handleSkipPartner}
                  variant="ghost"
                  className="w-full text-muted-foreground hover:text-foreground"
                >
                  Praleisti partnerį
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Privacy Info - Updated */}
        <div className="mt-8 text-center">
          <p className="text-muted-foreground text-sm">
            Tavo duomenys naudojami tik asmeninei analizei kurti.
          </p>
        </div>
      </div>

      {/* Birth Date Info Modal */}
      <BirthDateInfoModal
        isOpen={showBirthDateInfo}
        onClose={() => setShowBirthDateInfo(false)}
        onConfirm={handleBirthDateInfoConfirm}
      />

      {/* Guest Limit Modal */}
      {showGuestLimitModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="card-mystical max-w-md w-full p-6 relative animate-in fade-in zoom-in duration-300">
            {/* Close button */}
            <button
              onClick={() => setShowGuestLimitModal(false)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors"
              data-testid="guest-limit-close"
            >
              <ArrowLeft className="w-5 h-5 text-muted-foreground hover:text-foreground" />
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-amber-500/40">
                <Lock className="w-8 h-8 text-amber-400" />
              </div>
              
              <h3 className="font-display text-xl text-amber-400 mb-3">
                Pasiektas limitas
              </h3>
              
              <p className="text-foreground/80 mb-6 leading-relaxed">
                Jūs jau atlikote {MAX_GUEST_CALCULATIONS} nemokamus skaičiavimus. Užsiregistruokite nemokamai ir gaukite dar 7 vardų paskaičiavimus!
              </p>
              
              <div className="space-y-3">
                <Button
                  onClick={() => {
                    setShowGuestLimitModal(false);
                    setShowAuthModal(true);
                  }}
                  className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold py-5"
                  data-testid="guest-limit-register"
                >
                  <UserPlus className="w-5 h-5 mr-2" />
                  Registruotis nemokamai
                </Button>
                
                <button
                  onClick={() => setShowGuestLimitModal(false)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Grįžti
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          setShowAuthModal(false);
          // After successful registration, proceed with calculation
          handleFinalSubmit(false);
        }}
        defaultTab="register"
      />
    </div>
  );
}
